import { FC, forwardRef, useMemo } from "react";
import { Chip, Stack, TextField, Typography } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { InputProps as StandardInputProps } from "@mui/material/Input/Input";
import { FilterTriggerComponentProps } from "@/components/common/select/dropdown-select/utils/types/filterTriggerComponentProps";
import { GroupingOptionIcon } from "@/components/pages/common/resource-explorer-grouping-selector-v2/components/GroupingOptionIcon";

interface CsvDownloadDialogTriggerComponentProps
  extends FilterTriggerComponentProps {}

export const CsvDownloadDialogTriggerComponent: FC<
  CsvDownloadDialogTriggerComponentProps
> = ({ onClick, open, disabled, selectedValues, options, label }) => {
  const InputComponent: StandardInputProps["inputComponent"] = useMemo(() => {
    return forwardRef(() => (
      <TriggerInputComponent
        selectedValues={selectedValues}
        options={options}
        disabled={disabled}
        label={label}
      />
    ));
  }, [selectedValues, options, disabled, label]);

  return (
    <TextField
      size="medium"
      label={label}
      onClick={onClick}
      disabled={disabled}
      contentEditable={false}
      InputProps={{
        readOnly: true,
        inputComponent: InputComponent,
        endAdornment: open ? (
          <ArrowDropUpIcon fontSize="small" sx={{ color: "action" }} />
        ) : (
          <ArrowDropDownIcon fontSize="small" sx={{ color: "action" }} />
        ),
        inputProps: {
          style: {
            paddingLeft: 8,
            cursor: "pointer",
            caretColor: "transparent",
            textOverflow: "ellipsis",
            overflow: "hidden",
          },
        },

        sx: { pr: 1, cursor: "pointer", minWidth: 175 },
        startAdornment: (
          <GroupingOptionIcon
            grouping={
              label === "Dimensions" ? "category_v2" : "cost_allocation_tag"
            }
          />
        ),
      }}
    />
  );
};

const TriggerInputComponent: FC<
  Pick<
    FilterTriggerComponentProps,
    "selectedValues" | "options" | "disabled" | "label"
  >
> = ({ options, selectedValues, disabled, label }) => {
  const allSelected = selectedValues.length === options.length;

  let chipLabel: string = "";
  if (allSelected) {
    chipLabel = "All";
  } else if (selectedValues?.length) {
    chipLabel = `${selectedValues?.length}`;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      width="100%"
      height={40}
    >
      <Typography pl={1}>{label}</Typography>

      {!!selectedValues?.length && (
        <Chip
          size="small"
          variant="filled"
          label={chipLabel}
          disabled={disabled}
          sx={smallChipStyles}
        />
      )}
    </Stack>
  );
};

const smallChipStyles = {
  my: 0.25,
  maxHeight: 18,
  "& *": { fontSize: 12 },
  "& .MuiSvgIcon-root": { fontSize: 16, ml: 0 },
};
